import React from 'react';
import { Field, FieldRenderProps, FieldProps } from 'react-final-form';
import { RadioField } from '../RadioField';
import { RadioFieldV2Props } from '../RadioField/RadioFieldV2';

type RadioFieldInputProps<
  RP extends FieldRenderProps<string, T>,
  T extends HTMLElement = HTMLElement
> = Omit<FieldProps<string, RP, T>, 'type'> & RadioFieldV2Props;

export interface FinalFormRadioFieldProps<
  RP extends FieldRenderProps<string, T>,
  T extends HTMLElement = HTMLElement
> extends RadioFieldInputProps<RP, T> {
  name: string;
  id: string;
}

/**
 *
 * ```import FinalFormRadioField from '@zola/zola-ui/build/es/components/Form/RadioFieldV2/FinalFormRadioField```
 *
 * Renders a radio button in React Final Form using the Design System.
 *
 */
const FinalFormRadioField = <
  RP extends FieldRenderProps<string, T>,
  T extends HTMLElement = HTMLElement
>(
  props: FinalFormRadioFieldProps<RP, T>
): React.ReactElement => {
  const { type = 'radio', id, children, errorMessage, ...rest } = props;

  return (
    <Field type={type} {...rest}>
      {({ input, meta, onChange: onChangeFct, ...rest }) => {
        const hasError = meta && meta.touched && (meta.submitError || meta.error);
        const errorMessageString = hasError ? errorMessage || meta.submitError || meta.error : '';
        const inputProps = {
          ...input,
          ...rest,
          children,
          id,
          errorMessage: errorMessageString,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            input.onChange(e);
            if (onChangeFct) {
              onChangeFct(e);
            }
          },
        };
        return <RadioField {...inputProps} />;
      }}
    </Field>
  );
};

export default FinalFormRadioField;
